import React, { FC, useEffect, useState } from 'react'
import classes from './NftDisplayer.module.scss'
import { CircularProgress } from '@mui/material'
import { BounceContainer } from '@components/BounceContainer/BounceContainer'
import { logger } from '@plug-core/logger'
import { NFT } from '@plug-core/interfaces/common'

interface NFTDisplayerProps {
  nft: NFT
  className?: string
  onClick?: () => void
  interactive?: boolean
  defaultTag?: string
}

const TYPE_MAP: Record<string, keyof JSX.IntrinsicElements> = {
  'video/mp4': 'video',
  'image/png': 'img',
  'image/jpeg': 'img',
  'image/jpg': 'img',
  'image/svg+xml': 'img',
  'image/webp': 'img',
  'image/gif': 'img',
  'text/html': 'iframe',
  'text/html;charset=utf-8': 'iframe',
  'application/pdf': 'iframe',
}

const TAG_PROPS: Record<string, any> = {
  img: {
    width: '100%',
    height: '100%',
  },
  iframe: {
    width: '100%',
    height: '100%',
    loading: 'lazy',
  },
  video: {
    autoPlay: true,
    muted: true,
    loop: true,
    width: '100%',
    height: '100%',
  },
}

const NFTDisplayer: FC<NFTDisplayerProps> = ({
  nft,
  className,
  onClick,
  interactive,
  defaultTag,
}) => {
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState('image/png')

  const defaultType = nft.standard === 'ordinal' ? nft.contentType : null

  useEffect(() => {
    if (!defaultTag && !defaultType && nft.url) {
      fetch(nft.url)
        .then((response) => {
          setType(response.headers.get('Content-Type') || '')
        })
        .catch((e) => {
          logger.error('Failed to fetch NFT type', { originalError: e })
        })
    }
  }, [nft, defaultTag, defaultType])

  const resolvedType = defaultType || type
  const tagFromType = TYPE_MAP[resolvedType]
  if (!tagFromType && !defaultTag) {
    logger.debug(`Unknown MIME type: ${resolvedType}, falling back to img`)
  }

  const Tag = defaultTag || tagFromType || 'img'
  const customProps = TAG_PROPS[Tag] || {}

  if (Tag === 'iframe') {
    return (
      <div
        className={`${classes.iframeWrapper} ${interactive ? classes.interactive : className}`}
        onClick={onClick}
      >
        {!interactive && <span className={classes.iframeClick} />}
        <Tag className={classes.innerFrame} {...customProps} src={nft.url} />
      </div>
    )
  }

  return (
    <BounceContainer>
      <div className={`${className} ${classes.wrapper} ${loading ? classes.loadingWrapper : ''}`}>
        <Tag
          {...customProps}
          onClick={onClick}
          onLoad={() => setLoading(false)}
          onPlay={() => setLoading(false)}
          src={nft.url}
        />
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress size="25%" />
          </div>
        )}
      </div>
    </BounceContainer>
  )
}

export default NFTDisplayer
